/**
 * usePricing Hook
 *
 * Calculates booking pricing based on listing, dates, and the admin-managed
 * platform fee mode (host_pays / guest_pays / split). See services/platformFees.ts
 * for the math.
 */

import { useMemo } from 'react';
import {
  usePlatformFees,
  computeCommissionBreakdown,
  type FeeMode,
} from '@/services/platformFees';
import { formatDate } from '@/utils/dates';

export interface BookingPricing {
  nightlyRate: number;
  weekendRate?: number;
  nights: number;
  weekendNights: number;
  nightlyTotal: number;
  cleaningFee: number;
  // The dollar amount the platform takes (regardless of who pays it).
  platformCommission: number;
  // The dollar amount the GUEST is charged on top of subtotal - non-zero
  // only in guest_pays / split modes.
  guestServiceFee: number;
  taxes: number;
  securityDeposit: number;
  // What the guest actually pays.
  totalAmount: number;
  // What credits the host wallet on checkout.
  hostPayout: number;
  // Echo of the active fee mode and rate so UI can label things correctly.
  feeMode: FeeMode;
  commissionPercent: number;
  currency: string;
}

interface ListingPricing {
  nightlyRate: number;
  weekendRate?: number;
  cleaningFee?: number;
  securityDeposit?: number;
}

function differenceInDays(endDate: Date, startDate: Date): number {
  const diffTime = endDate.getTime() - startDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function isWeekendNight(date: Date): boolean {
  const day = date.getDay();
  return day === 5 || day === 6;
}

export const usePricing = (
  listingPricing: ListingPricing | undefined,
  checkIn: Date | null,
  checkOut: Date | null,
  customPrices?: Map<string, { price?: number }> | null
): BookingPricing | null => {
  const fees = usePlatformFees();

  return useMemo(() => {
    if (!listingPricing || !checkIn || !checkOut) {
      return null;
    }

    const nights = differenceInDays(checkOut, checkIn);
    if (nights <= 0) return null;

    let nightlyTotal = 0;
    let weekendNights = 0;

    for (let i = 0; i < nights; i++) {
      const date = addDays(checkIn, i);
      const isWeekend = isWeekendNight(date);
      const override = customPrices?.get(formatDate(date, 'numeric'))?.price;
      if (override != null) {
        nightlyTotal += override;
        if (isWeekend) weekendNights++;
      } else if (isWeekend && listingPricing.weekendRate) {
        nightlyTotal += listingPricing.weekendRate;
        weekendNights++;
      } else {
        nightlyTotal += listingPricing.nightlyRate;
      }
    }

    const cleaningFee = listingPricing.cleaningFee || 0;
    const securityDeposit = listingPricing.securityDeposit || 0;
    const subtotal = Math.round((nightlyTotal + cleaningFee) * 100) / 100;

    const breakdown = computeCommissionBreakdown(subtotal, fees);

    return {
      nightlyRate: listingPricing.nightlyRate,
      weekendRate: listingPricing.weekendRate,
      nights,
      weekendNights,
      nightlyTotal,
      cleaningFee,
      platformCommission: breakdown.platformCommission,
      guestServiceFee: breakdown.guestSurcharge,
      taxes: 0,
      securityDeposit,
      totalAmount: breakdown.guestTotal,
      hostPayout: breakdown.hostPayout,
      feeMode: breakdown.feeMode,
      commissionPercent: breakdown.commissionPercent,
      currency: 'USD',
    };
  }, [listingPricing, checkIn, checkOut, fees, customPrices]);
};

export default usePricing;
